import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// Generated by GitHub Copilot - Oct-08-2026
import { ActivatedRoute, NavigationSkipped, NavigationSkippedCode, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReviewerService } from '../core/services/reviewer.service';
import { MasterService } from '../core/services/master.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../core/services/user-role.service';
import { DateFormatService } from '../core/services/date-format.service';
import { ExcelExportService } from '../core/services/excel-export.service';
import { CreateNewRequestComponent } from './create-new-request/create-new-request.component';
 
interface ColumnItem {
  key: string;
  label: string;
  visible: boolean;
  width: number;
}
 
interface ColumnFilter {
  op1: string;
  val1: string;
}
 
@Component({
  standalone: true,
  selector: 'app-request',
  imports: [MatIconModule, CommonModule, FormsModule, CreateNewRequestComponent],
  templateUrl: './request.component.html',
  styleUrl: './request.component.css',
})
export class RequestComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private userRoleService = inject(UserRoleService);
  private dateFormatSvc = inject(DateFormatService);
  private excelExportSvc = inject(ExcelExportService);
 
  get isLimitedRole(): boolean {
    const role = this.userRoleService.rollName?.toLowerCase() ?? '';
    return role === 'reviewer' || role === 'r&c member' || role === 'supervisor';
  }
 
  isEditDisabled(item: any): boolean {
    const createdBy = (item.createdBy ?? '').toLowerCase();
    const rcMgr     = (item.rcManager ?? '').toLowerCase();
    const secRcMgr  = (item.secondaryRcManager ?? '').toLowerCase();
    const loggedIn  = (this.rebarAuth.getUserName() ?? '').toLowerCase();

    if (this.userRoleService.isAdmin) return false;

    const isInReviewDetails = !!loggedIn && (loggedIn === rcMgr || loggedIn === secRcMgr);
    if (!isInReviewDetails) return true;

    if (createdBy === rcMgr) {
      return secRcMgr ? loggedIn !== secRcMgr : true;
    }
    if (createdBy === secRcMgr) {
      return rcMgr ? loggedIn !== rcMgr : true;
    }
    return false;
  }
 
  get isDeleteDisabled(): boolean {
    return this.isLimitedRole || this.userRoleService.isManager;
  }
 
  pageSize = 10;
  currentPage = 1;
  goToPageInput = 1;
  sortColumn = 'requestedDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  isFilterOpen = false;
  isLoading = false;
  isExporting = false;
  searchText = '';
  columnSearchText = '';
  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];
  data: any[] = [];
  private defaultOrder: any[] = [];
 
  showCreateModal = false;
  showToast = false;
  toastMessage = '';
  private toastTimer: any;
 
  showDeleteModal = false;
  isDeletingRequest = false;
  private pendingDeleteItem: any = null;
 
  columns: ColumnItem[] = [
    { key: 'requestId', label: 'Request ID', visible: true, width: 12 },
    { key: 'requestType', label: 'Request Type', visible: true, width: 40 },
    { key: 'status', label: 'Status', visible: true, width: 9 },
    { key: 'requestedBy', label: 'Requested By', visible: true, width: 20 },
    { key: 'requestedDate', label: 'Requested Date', visible: true, width: 14.5 },
    { key: 'nameOfClient', label: 'Name of Client', visible: false, width: 16 },
    { key: 'dntProjectName', label: 'D&T Project Name', visible: false, width: 20 },
    { key: 'contractNumber', label: 'Contract Number', visible: false, width: 17 },
    { key: 'deliveryCountry', label: 'Delivery Country', visible: false, width: 17 },
    { key: 'city', label: 'City', visible: false, width: 14 },
    { key: 'isRepletterProject', label: 'Would you be creating same project in Repletter tool?', visible: false, width: 45 },
    { key: 'comments', label: 'Comments/Summery/Notes/Issue', visible: false, width: 30 },
    { key: 'assignedTo', label: 'Assigned To', visible: true, width: 22 },
  ];
 
  filterableColumns: string[] = [
    'requestId', 'requestType', 'requestedBy', 'requestedDate', 'status', 'nameOfClient',
    'dntProjectName', 'contractNumber', 'deliveryCountry', 'city',
    'isRepletterProject', 'comments', 'assignedTo',
  ];
 
  filters: { [key: string]: ColumnFilter } = {};
 
  constructor(private reviewerService: ReviewerService, private masterService: MasterService, private rebarAuth: RebarAuthService, private router: Router) {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
  }
 
  ngOnInit() {
    // Generated by GitHub Copilot - Oct-08-2026
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationSkipped &&
          event.code === NavigationSkippedCode.IgnoredSameUrlNavigation) {
        this.closeCreateModal();
      }
    });
    this.loadRequests();
    const navState = this.router.lastSuccessfulNavigation?.extras?.state ?? history.state;
    if (navState?.['approveSuccess']) {
      clearTimeout(this.toastTimer);
      this.toastMessage = 'Request approved successfully.';
      this.showToast = true;
      this.toastTimer = setTimeout(() => { this.showToast = false; }, 4000);
      history.replaceState({}, '', location.href);
    } else if (navState?.['rejectSuccess']) {
      clearTimeout(this.toastTimer);
      this.toastMessage = 'Request rejected successfully.';
      this.showToast = true;
      this.toastTimer = setTimeout(() => { this.showToast = false; }, 4000);
      history.replaceState({}, '', location.href);
    }
    if (this.route.snapshot.queryParamMap.get('new') === '1') {
      this.openCreateModal();
    }
  }

  private loadRequests(): void {
    this.isLoading = true;
    this.masterService.getRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = (res || []).map(item => {
            let rcManager = '';
            let secondaryRcManager = '';
            try {
              const details = typeof item.ReviewDetails === 'string'
                ? JSON.parse(item.ReviewDetails) : (item.ReviewDetails ?? []);
              rcManager          = details[0]?.RCManager          ?? '';
              secondaryRcManager = details[0]?.SecondaryRCManager ?? details[0]?.SecondaryRcManager ?? '';
            } catch { /* leave empty */ }
            return {
              requestId:          item.RequestID ?? '',
              requestType:        item.RequestType ?? '',
              status:             item.Status ?? '',
              requestedBy:        this.beforeAt(item.RequestedBy),
              requestedDate:      this.formatISODate(item.RequestedDateTime ?? ''),
              assignedTo:         item.AssignedTo ?? '',
              nameOfClient:       item.NameOfClient ?? '',
              operationServiceLine: item.OperationServiceLine ?? '',
              dntProjectName:     item.DTProjectName ?? '',
              contractNumber:     item.ContractNumber ?? '',
              deliveryCountry:    item.DeliveryCountry ?? '',
              city:               item.City ?? '',
              isRepletterProject: item.SameProjectInRepletterTool ?? '',
              comments:           item.Comments ?? '',
              createdBy:          this.beforeAt(item.CreatedBy),
              rcManager,
              secondaryRcManager,
            };
          });
          this.applyDefaultSort();
          this.defaultOrder = [...this.data];
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
 
  private beforeAt(value: any): string {
    const s = (value ?? '').toString();
    const idx = s.indexOf('@');
    return idx > 0 ? s.substring(0, idx) : s;
  }
 
  private formatISODate(value: string): string {
    return this.dateFormatSvc.format(value);
  }
 
  private parseSortableDate(val: string): number {
    const months: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };
    const parts = val.split('-');
    if (parts.length === 3) {
      const mon = months[parts[1].toLowerCase()];
      if (mon !== undefined) {
        return new Date(+parts[2], mon, +parts[0]).getTime();
      }
    }
    const ts = Date.parse(val);
    return isNaN(ts) ? 0 : ts;
  }
 
  private applyDefaultSort() {
    this.data.sort((a, b) => {
      if (this.sortColumn) {
        let cmp: number;
        if (this.sortColumn === 'requestedDate') {
          cmp = this.parseSortableDate(String(a.requestedDate ?? ''))
              - this.parseSortableDate(String(b.requestedDate ?? ''));
        } else {
          const va = String(a[this.sortColumn] ?? '').toLowerCase();
          const vb = String(b[this.sortColumn] ?? '').toLowerCase();
          cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
        }
        return this.sortDirection === 'asc' ? cmp : -cmp;
      }
      const va = String(a['requestId'] ?? '');
      const vb = String(b['requestId'] ?? '');
      return vb.localeCompare(va, undefined, { numeric: true, sensitivity: 'base' });
    });
  }
 
  filteredColumns(): ColumnItem[] {
    const q = (this.columnSearchText || '').toLowerCase().trim();
    return q ? this.columns.filter(c => c.label.toLowerCase().includes(q)) : this.columns;
  }
 
  private exportableColumns(): ColumnItem[] {
    return this.columns;
  }
 
  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : false;
  }
 
  visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }
 
  totalColumnCount(): number {
    return this.columns.length;
  }

  private exportData(): any[] {
    if (!this.defaultOrder.length) return this.data;
    const currentRows = new Set(this.data);
    const savedRows = new Set(this.defaultOrder);
    return [
      ...this.defaultOrder.filter(row => currentRows.has(row)),
      ...this.data.filter(row => !savedRows.has(row)),
    ];
  }
 
  async exportAll(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns().map(c => ({ key: c.key, header: c.label, width: c.width }));
      const fileName = this.buildRequestExportFileName('RequestListing_ExportAll');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Request Export All');
    } catch (err) {
      console.error('Request Export All failed:', err);
    } finally {
      this.isExporting = false;
    }
  }
 
  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns()
        .filter(c => c.visible)
        .map(c => ({ key: c.key, header: c.label, width: c.width }));
      const fileName = this.buildRequestExportFileName('RequestListing_ExportVisible', '-');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Request Export Visible');
    } catch (err) {
      console.error('Request Export Visible failed:', err);
    } finally {
      this.isExporting = false;
    }
  }
 
  private buildRequestExportFileName(prefix: string, separator: '' | '-' = '', date: Date = new Date()): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dd = String(date.getDate()).padStart(2, '0');
    return `${prefix}_${dd}${separator}${months[date.getMonth()]}${separator}${date.getFullYear()}`;
  }
 
  toggleFilterPanel() {
    this.isFilterOpen = !this.isFilterOpen;
  }
 
  toggleColumnFilter(col: string, event: Event) {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }
 
  applyFilter() {
    this.activeFilterColumn = null;
    this.currentPage = 1;
  }
 
  resetFilter(col: string) {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.activeFilterColumn = null;
    this.currentPage = 1;
  }
 
  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }
 
  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }
 
  filteredData(): any[] {
    const term = this.searchText.trim().toLowerCase();
    return this.data.filter(row => {
      if (term) {
        const matches = Object.values(row).some(v => String(v ?? '').toLowerCase().includes(term));
        if (!matches) return false;
      }
      return this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        return this.matchOperator(String((row as any)[col] ?? ''), f.op1, f.val1);
      });
    });
  }
 
  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredData().length / this.pageSize));
  }
 
  totalPagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 10;
    const startPage = Math.floor((this.currentPage - 1) / maxVisible) * maxVisible + 1;
    const endPage = Math.min(startPage + maxVisible - 1, total);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }
 
  goToPage() {
    if (this.goToPageInput >= 1 && this.goToPageInput <= this.totalPages()) {
      this.currentPage = this.goToPageInput;
    }
  }
 
  firstPage()  { this.currentPage = 1; this.goToPageInput = 1; }
  prevPage()   { if (this.currentPage > 1) this.currentPage--; }
  nextPage()   { if (this.currentPage < this.totalPages()) this.currentPage++; }
  lastPage()   { this.currentPage = this.totalPages(); this.goToPageInput = this.currentPage; }
 
  sort(column: string) {
    this.activeFilterColumn = null;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyDefaultSort();
    this.currentPage = 1;
  }
 
  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }
 
  get existingSlNames(): string[] {
    return this.data
      .filter(d => d.requestType === 'Accenture Operations Service Line' || d.requestType === 'Accenture Operation Service Line')
      .map(d => d.operationServiceLine as string)
      .filter(Boolean);
  }
 
  openCreateModal() {
    this.showCreateModal = true;
  }
 
  openRequest(item: any, mode: 'view' | 'edit') {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/request/view-edit'], {
        queryParams: {
          requestType: item.requestType,
          requestId: item.requestId,
          mode,
        },
      })
    );
    window.open(url, '_blank', 'noopener');
  }
 
  closeCreateModal() {
    this.showCreateModal = false;
  }
 
  onRequestSubmitted(requestType: string) {
    this.closeCreateModal();
    this.currentPage = 1;
    this.loadRequests();
 
    const messages: Record<string, string> = {
      'Add New User': 'The Request for New User has been sent successfully',
      'Accenture Operations Service Line': 'The Request for New SL has been sent successfully',
      'Accenture Operation Service Line': 'The Request for New SL has been sent successfully',
      'Request Not Applicable Plan Review Activity/Stage': 'The Request for New Not Applicable Request has been sent successfully',
    };
    clearTimeout(this.toastTimer);
    this.toastMessage = messages[requestType] ?? 'Request submitted successfully';
    this.showToast = true;
    this.toastTimer = setTimeout(() => { this.showToast = false; }, 4000);
  }
 
  dismissToast() {
    this.showToast = false;
  }
 
  openDeleteModal(item: any) {
    this.pendingDeleteItem = item;
    this.showDeleteModal = true;
  }
 
  closeDeleteModal() {
    this.showDeleteModal = false;
    this.pendingDeleteItem = null;
  }
 
  confirmDelete() {
    if (!this.pendingDeleteItem || this.isDeletingRequest) return;
    this.showDeleteModal = false;
    this.isDeletingRequest = true;
    const modifiedBy = this.rebarAuth.getUser() ?? '';
    this.masterService.deleteRequest(this.pendingDeleteItem.requestId, modifiedBy)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDeletingRequest = false;
          this.pendingDeleteItem = null;
          clearTimeout(this.toastTimer);
          this.toastMessage = 'Request deleted successfully.';
          this.showToast = true;
          this.toastTimer = setTimeout(() => { this.showToast = false; }, 4000);
          this.loadRequests();
        },
        error: () => {
          this.isDeletingRequest = false;
          this.pendingDeleteItem = null;
        },
      });
  }
 
  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    const clickedInsideTable = event.target.closest('table');
    const clickedInsideFilter = event.target.closest('.filter-dropdown');
    if (!clickedInsideTable && !clickedInsideFilter) {
      this.activeFilterColumn = null;
    }
  }
}
